import * as React from 'react';

import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useCoarsePointer } from '@/hooks/use-coarse-pointer';
import { cn } from '@/lib/utils';

type Side = 'top' | 'right' | 'bottom' | 'left';
type Align = 'start' | 'center' | 'end';

export interface HintProps {
  /** The hint body — plain text or rich content. */
  content: React.ReactNode;
  /**
   * The trigger element, rendered `asChild` — pass a single element that
   * accepts a ref and DOM props (a `<span>`, `<button>`, `<div>`, …).
   */
  children: React.ReactNode;
  /** Extra classes for the hint surface (tooltip content / popover content). */
  className?: string;
  side?: Side;
  align?: Align;
  /** Hover open-delay (ms) on pointer-fine devices. Ignored on touch (tap is instant). */
  hoverDelayMs?: number;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
}

/**
 * A supplementary hint that adapts to the input device: a hover/focus **tooltip**
 * on pointer-fine devices, and a tap-to-toggle **popover** on touch (no-hover)
 * devices.
 *
 * Radix `Tooltip` has no touch story — a tap opens it on pointer-enter and then
 * Radix dismisses it on the following press, so it visibly "blinks" and its
 * content is never actually reachable on a phone. Routing touch to `Popover`
 * makes the *same* content reachable with a single stable tap (tap again, tap
 * outside, or Escape to close). Use this for any icon/badge/chip whose meaning
 * would otherwise live only behind hover.
 *
 * For an **interactive** trigger (a text input, a link) don't wrap it here — a
 * tap-popover fights the primary action. Show the hint's content elsewhere on
 * touch instead (see `LineField`, which keeps the hover tooltip only on
 * pointer-fine devices).
 */
export function Hint({
  content,
  children,
  className,
  side,
  align,
  hoverDelayMs,
  open,
  defaultOpen,
  onOpenChange,
}: HintProps) {
  const coarse = useCoarsePointer();

  if (coarse) {
    return (
      <Popover open={open} defaultOpen={defaultOpen} onOpenChange={onOpenChange}>
        <PopoverTrigger asChild>{children}</PopoverTrigger>
        <PopoverContent
          side={side}
          align={align ?? 'center'}
          // Shrink the card-sized popover default down to a compact hint chip.
          className={cn('w-auto max-w-xs gap-0 p-2 text-xs', className)}
        >
          {content}
        </PopoverContent>
      </Popover>
    );
  }

  return (
    <Tooltip
      open={open}
      defaultOpen={defaultOpen}
      onOpenChange={onOpenChange}
      delayDuration={hoverDelayMs}
    >
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent side={side} align={align} className={className}>
        {content}
      </TooltipContent>
    </Tooltip>
  );
}
